// @flow

import * as React from 'react'
import { connect, type Connector } from 'react-redux'
import { Icon, Label, Segment, Header, List, Message, Button, Dimmer, Progress } from 'semantic-ui-react'

import { setParsePending, setParseError, setParseSuccess, resetParseData, uploadData } from '../store/xlsx-uploader'
import { closeSystemSidebar } from '../store/app'
import XLSXParser from './xlsx-parser'
import DownloadXLSX from './download-xlsx'

import type { ReduxState, Dispatch } from '../store'
import type { CheckRecordsCallback } from './xlsx-parser'

type OwnProps = {|
  strictColumnsCheck: boolean,
  updateStatus?: boolean,
  checkRecords?: boolean,
  checkRecordsCallback?: CheckRecordsCallback,
  columns: Array<string>,
  campaignName: string,
  chunkSize: number,
  title: string,
  uploadUrl: string,
|}

type MappedProps = {|
  state: $PropertyType<ReduxState, 'xlsxUploader'>,
|}

const mapStateToProps = (state: ReduxState, ownProps: OwnProps): MappedProps => {
  return {
    state: state.xlsxUploader,
  }
}

type Props = {| dispatch: Dispatch |} & MappedProps & OwnProps

class XLSXUploader extends React.Component<Props> {
  static defaultProps = {
    strictColumnsCheck: false,
    updateStatus: false,
    checkRecords: false,
    columns: [],
    campaignName: '',
    chunkSize: 50,
    uploadUrl: '',
  }

  componentDidMount() {
    this.props.dispatch(resetParseData())
  }

  onStartParse = filename => {
    this.props.dispatch(setParsePending(filename))
  }
  onEndParse = (cols: Array<string>, data: Array<Object>, message: string) => {
    this.props.dispatch(setParseSuccess(cols, data, message))
  }
  onAbortParse = msg => {
    this.props.dispatch(setParseError(msg))
  }
  onErrorParse = msg => {
    this.props.dispatch(setParseError(msg))
  }
  resetParseData = () => {
    this.props.dispatch(resetParseData())
  }

  submitData = async () => {
    this.props.dispatch(uploadData(this.props.uploadUrl, this.props.chunkSize))
  }

  onCloseSystemSidebar = () => {
    this.props.dispatch(closeSystemSidebar())
  }

  render() {
    let columns = this.props.columns.filter(el => {
      return el !== 'created_at'
    })
    let columnItems = { left: [], right: [] }
    for (let i = 0; i < columns.length; i++) {
      if (i < columns.length / 2) {
        columnItems.left.push(
          <List.Item as="li" key={'field-' + i}>
            {columns[i]}
          </List.Item>
        )
      } else {
        columnItems.right.push(
          <List.Item as="li" key={'field-' + i}>
            {columns[i]}
          </List.Item>
        )
      }
    }

    let filename = /panel/.test(this.props.title) ? 'tracciato_record_panel.xlsx' : 'tracciato_record_visite.xlsx'

    return (
      <Segment style={{ paddingTop: '6ex' }}>
        <Label attached="top" color="blue" size="big" style={{ zIndex: '700', width: '100%' }}>
          {this.props.title}
          <Icon name="close" style={{ float: 'right' }} onClick={this.onCloseSystemSidebar} />
        </Label>
        <Dimmer.Dimmable dimmed={this.props.state.submitPending}>
          <Dimmer
            active={this.props.state.submitPending}
            inverted
            style={{ zIndex: '700', backgroundColor: 'white', opacity: 0.95 }}>
            <Progress
              percent={this.props.state.submitPercent}
              style={{ width: '65%', margin: '0 auto' }}
              size="small"
              indicating>
              <div style={{ marginTop: '1ex' }}>
                Attendere, salvataggio dati in corso...
                <br />
                Non abbandonare questa pagina
              </div>
            </Progress>
          </Dimmer>
          <div className="ui centered stackable top aligned grid" style={{ fontSize: '0.95rem' }}>
            <div className="row">
              <div className="fifteen wide column">
                <Header>Campagna {this.props.campaignName}</Header>
              </div>
            </div>
            <div className="row">
              <div className="fifteen wide center aligned column">
                <XLSXParser
                  onStartParse={this.onStartParse}
                  onEndParse={this.onEndParse}
                  onError={this.onErrorParse}
                  onAbort={this.onAbortParse}
                  columns={columns}
                  strictColumnsCheck={this.props.strictColumnsCheck}
                  updateStatus={this.props.updateStatus}
                  checkRecords={this.props.checkRecords}
                  checkRecordsCallback={this.props.checkRecordsCallback}
                />
              </div>
            </div>
            <div className="row">
              {this.props.updateStatus && (
                <div className="fifteen wide column">
                  <Header>Il file deve essere in formato Excel 2007+ (xlsx) e contenere le seguenti colonne:</Header>
                  <ul>
                    <li>id</li>
                    <li>
                      <u>solo una</u> tra stato_f2f, stato_phone, stato_web
                    </li>
                  </ul>
                  <p>
                    <b>Nota:</b> saranno caricati i dati presenti esclusivamente nel primo foglio di lavoro e saranno
                    ignorati tutti i filtri attivi
                  </p>
                </div>
              )}
              {!this.props.updateStatus && (
                <div className="fifteen wide column">
                  <Header>
                    Il file deve essere in formato Excel 2007+ (xlsx) e contenere{' '}
                    <u>{this.props.strictColumnsCheck ? 'esattamente' : 'almeno'}</u> le seguenti colonne:
                  </Header>
                  <List as="ul">
                    <div className="ui grid">
                      <div className="eight wide left aligned column">{columnItems.left}</div>
                      <div className="eight wide left aligned column">{columnItems.right}</div>
                    </div>
                  </List>
                  <p>
                    <b>Nota:</b> saranno caricati i dati presenti esclusivamente nel primo foglio di lavoro e saranno
                    ignorati tutti i filtri attivi
                  </p>
                  <DownloadXLSX
                    style={{ float: 'right' }}
                    label="Scarica tracciato"
                    filename={filename}
                    data={[
                      columns.reduce((acc, e) => {
                        return { ...acc, [e]: undefined }
                      }, {}),
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </Dimmer.Dimmable>

        {this.props.state.parseStatus === 'ERROR' && (
          <Message negative>
            <Message.Header>Errore</Message.Header>
            {this.props.state.statusMessage}
          </Message>
        )}
        {this.props.state.parseStatus === 'SUCCESS' &&
          !this.props.state.submitPending && (
            <Message positive style={{ paddingBottom: '7.5ex' }}>
              <Message.Header>Analisi file completa</Message.Header>
              {"L'analisi del file è terminata e il file è risultato corretto."}
              <br />
              {'Proseguendo verranno caricati ' + this.props.state.parseData.rows.length + ' records'}
              <br />
              {this.props.state.statusMessage !== 'OK' && this.props.state.statusMessage}
              <div style={{ height: '1ex' }} />
              {this.props.state.parseData.rows.length > 0 && (
                <Button
                  color="green"
                  labelPosition="left"
                  className="right floated"
                  style={{ width: '18ex' }}
                  icon="save"
                  content="Prosegui"
                  onClick={this.submitData}
                />
              )}
              <Button
                labelPosition="left"
                className="right floated"
                style={{ width: '18ex' }}
                icon="cancel"
                content="Annulla"
                onClick={this.resetParseData}
              />
            </Message>
          )}
      </Segment>
    )
  }
}

const connector: Connector<OwnProps, MappedProps> = connect(mapStateToProps, null)
export default connector(XLSXUploader)



// WEBPACK FOOTER //
// ./src/systemwide/xlsx-uploader.js